import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { EmptyWrapper } from '@/_pages/vfo/analytics/_pageModule/components/EmptyWrapper';
import {
  AnalyticsOverview,
  DocumentsDashboard,
  MembersDashboard,
  TasksDashboard,
} from '@/features/vfo/components';
import { useAnalytics } from '@/features/vfo/hooks/useAnalytics';
import { useCases } from '@/features/vfo/hooks/useCases';
import { useMembers } from '@/features/vfo/hooks/useMembers';
import { Select } from '@/features/vfo/uikit/Select';
import { breakpoints, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { Button } from '@/shared/uikitV2/Button';
import { Checkbox } from '@/shared/uikitV2/Checkbox';
import { dates } from '@/shared/utils/dates';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { PrintReportModalProps } from '@/widgets/modals/types';

import { ModalContainerV2 } from '../../interwill/containers/ModalContainerV2';

import { useReportGeneration } from './useReportGeneration';

const GLOBAL_CASE_ID = 'global';

export const PrintReportModal: FC<PrintReportModalProps> = ({ defaultCaseId }) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'printReport' });
  const { generateReport, loading } = useReportGeneration();

  const [selectedCase, setSelectedCase] = useState(defaultCaseId ?? GLOBAL_CASE_ID);
  const [checkedItems, setCheckedItems] = useState(
    new Set(['overview', 'taskManager', 'members', 'documentStorage']),
  );

  const reportRef = useRef<HTMLDivElement>(null);

  const modals = useModalsStore();
  const { cases, findCase } = useCases();
  const { membersWithoutCurrentUser } = useMembers();
  const caseId = selectedCase === GLOBAL_CASE_ID ? undefined : selectedCase;

  const { analyticsData } = useAnalytics(caseId);

  const caseData = findCase(caseId);

  const membersFiltered = useMemo(() => {
    return caseData
      ? membersWithoutCurrentUser.filter((item) =>
          caseData.members.some((m) => m.profileId === item.profileId),
        )
      : membersWithoutCurrentUser;
  }, [caseData, membersWithoutCurrentUser]);

  const selectCases = useMemo(() => {
    const arr = cases.map((item) => ({
      label: item.title,
      value: item.id,
    }));
    arr.unshift({
      label: t('global'),
      value: GLOBAL_CASE_ID,
    });
    return arr;
  }, [cases]);

  const handleCheckboxChange = (key: string) => {
    setCheckedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleGenerateReport = () => {
    const name = `Report ${
      caseData ? caseData?.title : `Global ${dates.formatLongDate(new Date())}`
    }.png`;
    generateReport(reportRef, (dataUrl) => {
      if (!dataUrl) return;
      modals.closeModal();
      modals.openModal({
        type: 'FilePreviewModal',
        mode: 'fileInfo',
        fileInfo: {
          fileType: 'image',
          name,
          url: dataUrl,
        },
      });
    });
  };

  return (
    <ModalContainerV2 pinBottom title={t('title')}>
      <Container>
        <SelectStyled
          options={selectCases}
          value={selectedCase || GLOBAL_CASE_ID}
          onChange={(value) => {
            if (!value) {
              setSelectedCase(GLOBAL_CASE_ID);
            }
            if (typeof value === 'string') {
              setSelectedCase(value);
            }
          }}
        />
        <List>
          <CheckboxWrapper>
            <Checkbox
              checked={checkedItems.has('overview')}
              onChange={() => handleCheckboxChange('overview')}
            />
            <span>{t('overview')}</span>
          </CheckboxWrapper>
          <CheckboxWrapper>
            <Checkbox
              checked={checkedItems.has('taskManager')}
              onChange={() => handleCheckboxChange('taskManager')}
            />
            <span>{t('taskManager')}</span>
          </CheckboxWrapper>
          <CheckboxWrapper>
            <Checkbox
              checked={checkedItems.has('members')}
              onChange={() => handleCheckboxChange('members')}
            />
            <span>{t('members')}</span>
          </CheckboxWrapper>
          <CheckboxWrapper>
            <Checkbox
              checked={checkedItems.has('documentStorage')}
              onChange={() => handleCheckboxChange('documentStorage')}
            />
            <span>{t('documentStorage')}</span>
          </CheckboxWrapper>
        </List>
      </Container>

      <HiddenReportContainer ref={reportRef}>
        <ReportHeader>
          <b>
            {selectedCase === GLOBAL_CASE_ID
              ? t('global')
              : cases.find((c) => c.id === selectedCase)?.title}
          </b>
          <span>{dates.formatLongDate(new Date())}</span>
        </ReportHeader>

        <ReportBody>
          {checkedItems.has('overview') && (
            <Section>
              <SectionTitle>{t('overview')}</SectionTitle>
              <EmptyWrapper
                isEmpty={
                  !analyticsData ||
                  (analyticsData?.taskManager.isEmpty && analyticsData.allDocuments.isEmpty)
                }
              >
                <AnalyticsOverview analyticsData={analyticsData} />
              </EmptyWrapper>
            </Section>
          )}

          {checkedItems.has('taskManager') && (
            <Section>
              <SectionTitle>{t('taskManager')}</SectionTitle>
              <EmptyWrapper isEmpty={!analyticsData || analyticsData?.taskManager.isEmpty}>
                <TasksDashboard analyticsData={analyticsData} />
              </EmptyWrapper>
            </Section>
          )}

          {checkedItems.has('members') && (
            <Section>
              <SectionTitle>{t('members')}</SectionTitle>
              <EmptyWrapper isEmpty={!analyticsData || analyticsData.allMembers.totalCount <= 1}>
                <MembersDashboard
                  analyticsData={analyticsData}
                  members={membersFiltered}
                  caseId={caseId}
                />
              </EmptyWrapper>
            </Section>
          )}

          {checkedItems.has('documentStorage') && (
            <Section>
              <SectionTitle>{t('documentStorage')}</SectionTitle>
              <EmptyWrapper isEmpty={!analyticsData || analyticsData.allDocuments.isEmpty}>
                <DocumentsDashboard analyticsData={analyticsData} />
              </EmptyWrapper>
            </Section>
          )}
        </ReportBody>
      </HiddenReportContainer>

      <Buttons>
        <Button size="middle" onClick={handleGenerateReport} loading={loading}>
          {t('generateBtn')}
        </Button>
      </Buttons>
    </ModalContainerV2>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
  padding: 24px 0;
  max-height: 500px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 16px 0 0 0;

  ${media.md}, ${breakpoints.sm} {
    flex-direction: column;
    margin-top: auto;
  }
`;

const SelectStyled = styled(Select)`
  height: 44px;

  .field-view {
    background: #fff;
    height: 44px;
    border-color: ${COLORS.systemDark100};
  }

  .label {
    display: none;
  }

  .selected-label {
    bottom: 10px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 12px;
  cursor: pointer;
`;

const HiddenReportContainer = styled.div`
  position: fixed;
  top: 0;
  left: -9999px;
  width: 1200px;
  z-index: -1;
  pointer-events: none;
  padding: 40px 74px;
  transform: scale(0.5);

  .report-content {
    background: ${COLORS.white};
    padding: 40px;
    display: flex;
    flex-direction: column;
    gap: 32px;
  }
`;

const ReportHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  margin-bottom: 16px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    color: ${COLORS.systemDark300};
    margin: 0;
  }

  p {
    font-size: 14px;
    color: ${COLORS.systemDark300};
    margin: 0;
  }
`;

const ReportBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  break-inside: avoid;
  border-bottom: 1px solid ${COLORS.systemDark100};
  padding-bottom: 24px;

  &:last-of-type {
    border-bottom: none;
  }
`;

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.systemDark300};
  text-align: center;
`;
